//
// Saved Weight Management reviews (4 week and 6 month). Prototype storage: an external store backed by localStorage,
// so a saved review survives a reload. Entries are append-only: there is no edit, delete or clear (Part 3: audit).
// Invatech's back end replaces this with real storage; the shape below is what it needs to keep.
//

import { useSyncExternalStore } from "react";
import { registerFoundayoNeverRestartLookup } from "@/lib/pharmacyFirst/pathways/aht/_shared/foundayo-never-restart";

export type ReviewKind = "4week" | "6month";

export interface SavedReview {
  id: string;
  /** Treatment plan the review belongs to. Null when the review was not started from a plan. */
  planId: string | null;
  /** Latest supply event on that plan when the review was saved. */
  supplyEventId: string | null;
  patientId: string | null;
  kind: ReviewKind;
  drugId: string;
  savedAt: string;
  data: Record<string, unknown>;
}

const STORAGE_KEY = "aht-weight-saved-reviews-v1";

function readStorage(): SavedReview[] {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as SavedReview[]) : [];
  } catch {
    return [];
  }
}

let reviews: readonly SavedReview[] = readStorage();
const listeners = new Set<() => void>();

function persist(): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, JSON.stringify(reviews));
  } catch {
    // Storage full or unavailable: the review stays in memory for this session.
  }
}

export const reviewRecords = {
  get: (): readonly SavedReview[] => reviews,
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Adds a review and returns it. Existing entries are never changed. */
  append(entry: Omit<SavedReview, "id" | "savedAt">): SavedReview {
    const saved: SavedReview = {
      ...entry,
      id: `rev-${Date.now().toString(36)}-${reviews.length + 1}`,
      savedAt: new Date().toISOString(),
    };
    reviews = [...reviews, saved];
    persist();
    listeners.forEach((l) => l());
    return saved;
  },
  forPlan: (planId: string): SavedReview[] => reviews.filter((r) => r.planId === planId),
  /** Latest saved value of a field for a plan, newest first. */
  latestValue(planId: string, field: string): unknown {
    for (let i = reviews.length - 1; i >= 0; i--) {
      const r = reviews[i]!;
      if (r.planId === planId && r.data[field] !== undefined && r.data[field] !== null && r.data[field] !== "") return r.data[field];
    }
    return undefined;
  },
  __resetForTests(): void {
    reviews = [];
    try {
      if (typeof localStorage !== "undefined") localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    listeners.forEach((l) => l());
  },
};

export function useSavedReviews(): readonly SavedReview[] {
  return useSyncExternalStore(reviewRecords.subscribe, reviewRecords.get);
}

registerFoundayoNeverRestartLookup((patientId) =>
  reviews.some((r) => r.patientId === patientId && r.drugId === "foundayo" && r.data["foundayoNeverRestart"] === true),
);
